import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import * as ContentActions from '../../../../actions/content'
import { Centered, Confirmation, Headings } from '../../../../components'
import Button, { Sizes as ButtonSizes } from '../../../../components/Button'
import Padding, { Sizes as PaddingSizes } from '../../../../components/Padding'
import { ACL, colors } from '../../../../constants'
import * as MediaSelectors from '../../../../reducers/content/media/selectors'
import Media, { MediaPushStatusValues } from '../../../../types/Media'
import { EstateUtils } from '../../../../utils'

export default function PushStatus() {
    const dispatch = useDispatch()
    const selectedMedia: Media = useSelector(MediaSelectors.selectedMedia)
    const canPushMedia = useSelector((state) => EstateUtils.hasPermission(state, { acls: [ACL.CONTENT_PUSH] }))
    const [description, setDescription] = React.useState('')
    const { t } = useTranslation()

    const { id: mediaId, media_push_status: mediaPushStatus = [] } = selectedMedia || {}

    // Push status variables
    const { status: pushStatus } = mediaPushStatus[0] || {}

    const onPushOk = () => {
        dispatch(ContentActions.setMediaPushStatus(mediaId, true))
    }

    const onPushFailed = () => {
        dispatch(ContentActions.setMediaPushStatus(mediaId, false))
    }

    const onChangeDescription = (event: React.FormEvent) => {
        const target = event.target as HTMLTextAreaElement
        const value = target.value as string
        setDescription(value)
    }

    switch (pushStatus) {
        /**
         * Completed
         */
        case MediaPushStatusValues.Completed:
            return (
                <Padding top size={PaddingSizes.md}>
                    <Padding bottom size={PaddingSizes.xs}>
                        <Centered horizontal vertical>
                            <Headings.H3>{t('media-push-ok')}</Headings.H3>
                            {canPushMedia && (
                                <Confirmation
                                    cancel={t('cancel')}
                                    confirmation={t('media-push-failed_description')}
                                    header={t('media-push-failed_title')}
                                    ok={t('ok')}
                                    onCancel={() => null}
                                    onConfirm={onPushFailed}
                                >
                                    <Button size={ButtonSizes.md} theme="lightDanger">
                                        {t('media-push-failed-set')}
                                    </Button>
                                </Confirmation>
                            )}
                        </Centered>
                    </Padding>
                </Padding>
            )
        /**
         * Failed
         */
        case MediaPushStatusValues.Failed:
            return (
                <Padding top size={PaddingSizes.md}>
                    <Padding bottom size={PaddingSizes.xs}>
                        <Centered horizontal vertical>
                            <Headings.H3 color={colors.reddark}>{t('media-push-failed')}</Headings.H3>
                            {canPushMedia && (
                                <Confirmation
                                    cancel={t('cancel')}
                                    confirmation={t('media-push-ok_description')}
                                    header={t('media-push-ok_title')}
                                    ok={t('ok')}
                                    onCancel={() => null}
                                    onConfirm={onPushOk}
                                >
                                    <Button size={ButtonSizes.md} theme="light">
                                        {t('media-push-ok-set')}
                                    </Button>
                                </Confirmation>
                            )}
                        </Centered>
                    </Padding>
                </Padding>
            )
        /**
         * Pending
         */
        case MediaPushStatusValues.Pending:
        default:
            return (
                <Padding top size={PaddingSizes.md}>
                    <Padding bottom size={PaddingSizes.xs}>
                        <FontAwesomeIcon icon="exclamation-circle" />
                        &nbsp;
                        <Headings.H3 bold>{t('media-not-pushed')}</Headings.H3>
                    </Padding>
                    {canPushMedia && (
                        <>
                            <Padding autoWidth inline right size={PaddingSizes.sm}>
                                <Confirmation
                                    cancel={t('cancel')}
                                    confirmation={t('media-push-ok_description')}
                                    header={t('media-push-ok_title')}
                                    ok={t('ok')}
                                    onCancel={() => null}
                                    onConfirm={onPushOk}
                                >
                                    <Button size={ButtonSizes.md}>{t('media-push-ok-set')}</Button>
                                </Confirmation>
                            </Padding>
                            <Confirmation
                                cancel={t('cancel')}
                                confirmation={t('media-push-failed_description')}
                                header={t('media-push-failed_title')}
                                ok={t('ok')}
                                onCancel={() => setDescription('')}
                                onChangeText={onChangeDescription}
                                onConfirm={onPushFailed}
                                text={description}
                            >
                                <Button size={ButtonSizes.md} theme="danger">
                                    {t('media-push-failed-set')}
                                </Button>
                            </Confirmation>
                        </>
                    )}
                </Padding>
            )
    }
}
